import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { useApi } from '../../contexts/ApiProvider';
import { useNavigate } from 'react-router-dom';
import NewTripModal from './NewTripModal';

export default function TravelDashboard() {
    const api = useApi();
    const navigate = useNavigate();

    const [trips, setTrips] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [stats, setStats] = useState({ total_trips: 0, completed_trips: 0, active_trips: 0, total_days: 0 });

    useEffect(() => {
        fetchTrips();
        fetchStats(); // ADD THIS
        // eslint-disable-next-line
    }, []);

    // ADD THIS FUNCTION
    const fetchStats = async () => {
        const res = await api.get('/travel/stats');
        if (res.ok) setStats(res.body);
    };

    const fetchTrips = async () => {
        setIsLoading(true);
        const res = await api.get('/travel/trips');
        if (res.ok) setTrips(res.body.items || []);
        setIsLoading(false);
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'Planning': return 'warning';
            case 'Active': return 'success';
            case 'Completed': return 'secondary';
            default: return 'primary';
        }
    };

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-5">
                <div>
                    <h2 className="fw-bold mb-0"><i className="bi bi-globe-americas text-primary me-3"></i>Command Center</h2>
                    <p className="text-muted mb-0 mt-1">Manage logistics, packing, and budgets across your travel groups.</p>
                </div>
                <Button variant="primary" className="rounded-pill px-4 fw-bold shadow-sm" onClick={() => setShowModal(true)}>
                    <i className="bi bi-plus-lg me-2"></i>New Trip
                </Button>
                <Button variant="outline-primary" className="rounded-pill px-4 fw-bold shadow-sm me-2" onClick={() => navigate('/travel/closet')}>
                    <i className="bi bi-box2-heart me-2"></i>Gear Closet
                </Button>
            </div>

            {/* --- TRAVEL ANALYTICS --- */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-gradient text-dark h-100" style={{ background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)' }}>
                        <Card.Body className="d-flex align-items-center p-4">
                            <div className="bg-white p-3 rounded-circle shadow-sm me-4 text-primary">
                                <i className="bi bi-airplane-engines-fill fs-3"></i>
                            </div>
                            <div>
                                <h6 className="text-muted fw-bold mb-1 uppercase letter-spacing-1 small">Total Trips</h6>
                                <h3 className="fw-bold mb-0">{stats.total_trips}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-gradient text-dark h-100" style={{ background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)' }}>
                        <Card.Body className="d-flex align-items-center p-4">
                            <div className="bg-white p-3 rounded-circle shadow-sm me-4 text-success">
                                <i className="bi bi-calendar-heart-fill fs-3"></i>
                            </div>
                            <div>
                                <h6 className="text-muted fw-bold mb-1 uppercase letter-spacing-1 small">Days Exploring</h6>
                                <h3 className="fw-bold mb-0">{stats.total_days}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-gradient text-dark h-100" style={{ background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)' }}>
                        <Card.Body className="d-flex align-items-center p-4">
                            <div className="bg-white p-3 rounded-circle shadow-sm me-4 text-warning">
                                <i className="bi bi-compass-fill fs-3"></i>
                            </div>
                            <div>
                                <h6 className="text-muted fw-bold mb-1 uppercase letter-spacing-1 small">Active & Upcoming</h6>
                                <h3 className="fw-bold mb-0">{stats.active_trips}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {isLoading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : trips.length === 0 ? (
                <div className="bg-light p-5 rounded-4 text-center border text-muted">
                    <i className="bi bi-luggage display-4 mb-3 opacity-50"></i>
                    <h4>No trips on the radar.</h4>
                    <p>Click "New Trip" to spin up a budget and packing checklist.</p>
                </div>
            ) : (
                <Row className="g-4">
                    {trips.map(trip => (
                        <Col md={6} xl={4} key={trip.id}>
                            <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                                <Card.Body className="p-4 d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <Badge bg={`${getStatusColor(trip.status)} bg-opacity-10 text-${getStatusColor(trip.status)} px-3 py-2 rounded-pill border border-${getStatusColor(trip.status)}`}>
                                            <i className="bi bi-circle-fill me-2" style={{ fontSize: '8px' }}></i>
                                            {trip.status}
                                        </Badge>
                                        <span className="small text-muted fw-bold"><i className="bi bi-people-fill me-1"></i> {trip.group_name}</span>
                                    </div>

                                    <h4 className="fw-bold text-truncate mb-2" title={trip.location}>{trip.location}</h4>

                                    <div className="d-flex text-muted small fw-bold mb-4">
                                        <span><i className="bi bi-calendar3 me-1"></i> {new Date(trip.start_date).toLocaleDateString()}</span>
                                        <span className="mx-2"><i className="bi bi-arrow-right"></i></span>
                                        <span>{new Date(trip.end_date).toLocaleDateString()}</span>
                                    </div>

                                    <div className="mt-auto d-flex justify-content-between align-items-center pt-3 border-top">
                                        <div className="text-muted small fw-bold">
                                            {trip.budget_bucket_id ? (
                                                <span className="text-success"><i className="bi bi-safe2-fill me-1"></i> Budget Linked</span>
                                            ) : (
                                                <span><i className="bi bi-safe2 me-1"></i> Unlinked</span>
                                            )}
                                        </div>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="rounded-pill fw-bold"
                                            onClick={() => navigate(`/travel/${trip.id}`)}
                                        >
                                            Enter <i className="bi bi-chevron-right ms-1"></i>
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <NewTripModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSuccess={fetchTrips}
            />
        </Container>
    );
}
